/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import LabelStatus from 'components/labels/LabelStatus';
import { FontAwesomeIconStatus } from 'components/icon/FontAwesomeIconStatus';
import UserService from 'services/UserService';
import { MINHAAGENDA_PHONE } from 'commons/constants';
import WhatsUtils from 'commons/utils/whatsUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ButtonStatus from 'components/buttons/ButtonStatus';
import { currentUserSelector, useAppStore } from 'stores/appStore';
import { isAppTypeCourtRental } from 'commons/helpers/appTypeSettings';

const NUMBER_OF_EMPLOYEE_OPTIONS = [
  { maxNumberUsers: 6, label: '4 a 6' },
  { maxNumberUsers: 9, label: '7 a 9' },
  { maxNumberUsers: 12, label: '10 a 12' },
  { maxNumberUsers: 13, label: '13 ou mais' },
];

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  text: {},
  btnRequest: {
    margin: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
    textTransform: 'initial',
  },

  btnRequestLabelContainer: { flexDirection: 'row', alignItems: 'center', display: 'flex' },
  btnRequestLabelIndicator: {
    textTransform: 'initial',
    backgroundColor: '#06faa9',
    paddingLeft: '10px',
    paddingRight: '10px',
    paddingTop: '4px',
    paddingBottom: '4px',
    marginLeft: '5px',
    marginRight: '5px',
    borderRadius: 50,
    borderWidth: '1px',
    borderColor: '#00E096',
  },
  btnRequestLabelColor: { color: 'white', fontWeight: 'bold' },

  text1: { fontSize: '1.1rem' },
  text2: {
    fontSize: '1.2rem',
    fontWeight: '400',
    textAlign: 'center',
    marginTop: '10px',
  },
  text3: {
    fontSize: '1.4rem',
    fontWeight: 'bold',
    textAlign: 'center',
  },

  faqHint: {
    paddingTop: theme.spacing(1),
    color: theme.palette.grey[600],
  },
  containerBtnSupport: {
    paddingTop: theme.spacing(1),
  },
  divider: {
    paddingTop: theme.spacing(1),
    width: '100%',
  },
  iconWhats: {
    marginRight: '8px',
  },

  loadingContainer: { textAlign: 'center', marginTop: '10px' },
  loadingBar: { marginTop: '10px' },

  downArrowContainer: { marginTop: '10px', marginBottom: '10px' },
}));

// COMPONENT
function PlanUpgradeViaWhatsModal({ open, onClose }) {
  const { classes } = useStyles();
  const currentUser = useAppStore(currentUserSelector);
  const isCourtRental = isAppTypeCourtRental();

  const [isLoadingScreen, setIsLoadingScreen] = useState(false);
  const [accountStatus, setAccountStatus] = useState();

  useEffect(() => {
    if (open) {
      setIsLoadingScreen(true);
      UserService.getAccountStatus()
        .then((userAccountStatus) => {
          setAccountStatus(userAccountStatus);
        })
        .finally(() => {
          setIsLoadingScreen(false);
        });
    }
  }, [open]);

  const handleRequestAccess = (displayString) => {
    const message = `Solicitação upgrade usuários (${displayString}):\nNome: ${currentUser?.principal?.name}\nEmail: ${currentUser?.principal?.email}\nPlataforma: WEB`;

    WhatsUtils.sendMessage({
      phone: MINHAAGENDA_PHONE,
      text: message,
      shouldNotCleanUpNumber: true,
    });
  };

  const currentUserPlan = accountStatus?.maxNumberUsers ?? 3;

  const options = React.useMemo(() => {
    return NUMBER_OF_EMPLOYEE_OPTIONS.filter((p) => p.maxNumberUsers > currentUserPlan);
  }, [currentUserPlan]);

  return (
    <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" color="primary" component="span">
          Upgrade
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoadingScreen ? (
          <div className={classes.loadingContainer}>
            <Typography variant="h4" gutterBottom>
              Carregando...
            </Typography>
            <CircularProgress size={50} className={classes.loadingBar} thickness={5} />
          </div>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography gutterBottom className={classes.text1}>
              Seu plano atual é {currentUserPlan - 2}-{currentUserPlan} usuários/
              {isCourtRental ? 'quadras' : 'profissionais'}
            </Typography>

            {/* BODY */}
            <LabelStatus component="div" status="primary" className={classes.text2}>
              Faça o upgrade <b>clicando</b> abaixo e {'\n'}
              <LabelStatus status="primary" className={classes.text3}>
                aumente seu número de usuários
              </LabelStatus>
            </LabelStatus>
            <div className={classes.downArrowContainer}>
              <FontAwesomeIconStatus icon="arrow-down" size="2x" status="primary" />
            </div>

            {options.map((numOfEmployeesObj) => (
              <Button
                className={classes.btnRequest}
                key={numOfEmployeesObj.maxNumberUsers}
                onClick={() => {
                  handleRequestAccess(numOfEmployeesObj.label);
                }}
                fullWidth
              >
                <div className={classes.btnRequestLabelContainer}>
                  <Typography className={classes.btnRequestLabelColor} component="span">
                    De{' '}
                  </Typography>
                  <div className={classes.btnRequestLabelIndicator}>
                    <Typography component="span">{numOfEmployeesObj.label}</Typography>
                  </div>
                  <Typography className={classes.btnRequestLabelColor}>
                    {' '}
                    usuários/{isCourtRental ? 'quadras' : 'profissionais'}
                  </Typography>
                </div>
              </Button>
            ))}
            {/* BODY */}

            <div className={classes.divider}>
              <Divider />
            </div>
            <Typography className={classes.faqHint}>Em caso de dúvidas, contate nosso suporte</Typography>

            <div className={classes.containerBtnSupport}>
              <ButtonStatus
                status="success"
                variant="contained"
                onClick={() => {
                  const message = `Informações para agilizar o atendimento (Dúvida sobre a assinatura):\nNome: ${currentUser?.principal?.name}\nEmail: ${currentUser?.principal?.email}\nPlataforma: WEB`;

                  WhatsUtils.sendMessage({
                    phone: MINHAAGENDA_PHONE,
                    text: message,
                    shouldNotCleanUpNumber: true,
                  });
                }}
              >
                <FontAwesomeIcon
                  icon={['fab', 'whatsapp']}
                  color="white"
                  size="lg"
                  fixedWidth
                  className={classes.iconWhats}
                />
                Contatar o suporte
              </ButtonStatus>
            </div>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} fullWidth>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PlanUpgradeViaWhatsModal;
